import { useCallback, useState } from 'react'

import { Alert, Button, Group, Stack, Tabs, Text } from '@mantine/core'
import { IconInfoCircle } from '@tabler/icons-react'
import {
  createFileRoute,
  notFound,
  redirect,
  useNavigate,
} from '@tanstack/react-router'

import {
  vault_symbols,
  type VaultSymbol,
  vaultToFundSymbol,
} from '@repo/common/queries/products'
import { useVaultInfo } from '@repo/common/queries/vaults'

import { ContentBox, ContentWithTitle } from '@/components/ContentBox'
import { Holdings } from '@/components/Holdings'
import { VaultPerShareChart } from '@/components/VaultPerShareChart'
import { VaultReturnMetrics } from '@/components/VaultReturnMetrics'
import { VaultRow } from '@/components/VaultRow'
import { ManualPosition } from '@/flows/ManualPosition'
import { VaultDeposit } from '@/flows/VaultDeposit'
import { VaultRedeem } from '@/flows/VaultRedeem'

export function ensure_vault_symbol({
  symbol: raw_symbol,
}: {
  symbol: string
}): {
  symbol: VaultSymbol
} {
  const symbol = raw_symbol.toUpperCase() as VaultSymbol
  if (!vault_symbols.includes(symbol)) {
    throw notFound()
  }
  return { symbol }
}

export const Route = createFileRoute('/_app/vault/$symbol/')({
  component: VaultPage,
  onError: () =>
    redirect({
      throw: true,
      to: '/vault/$symbol',
      params: { symbol: vault_symbols[0] },
    }),
  params: {
    parse: ensure_vault_symbol,
  },
})

// eslint-disable-next-line max-lines-per-function
function VaultPage() {
  const { symbol } = Route.useParams()
  const fund_symbol = vaultToFundSymbol(symbol)

  const { data: vault, isLoading } = useVaultInfo({ symbol })
  const [active_tab, set_active_tab] = useState<string | null>('deposit')

  const navigate = useNavigate()
  const onCompleted = useCallback(() => {
    void navigate({ to: '/portfolio' })
  }, [navigate])

  if (isLoading || vault == null) return null

  return (
    <>
      <Stack>
        <VaultRow symbol={symbol} variant="details" />
        <Tabs
          keepMounted={false}
          value={active_tab}
          onChange={set_active_tab}
          variant="pills"
        >
          <Tabs.List mb="sm">
            <Tabs.Tab value="deposit">Deposit</Tabs.Tab>
            <Tabs.Tab value="redeem">Redeem</Tabs.Tab>
            <Tabs.Tab value="manual">Manual Position</Tabs.Tab>
          </Tabs.List>

          <Group
            w="100%"
            mt="lg"
            gap="calc(2*var(--mantine-spacing-xxl))"
            align="flex-start"
          >
            <Stack w="min-content">
              <Tabs.Panel value="deposit">
                <VaultDeposit symbol={symbol} onCompleted={onCompleted} />
              </Tabs.Panel>
              <Tabs.Panel value="redeem">
                <VaultRedeem symbol={symbol} onCompleted={onCompleted} />
              </Tabs.Panel>
              <Tabs.Panel value="manual">
                <Stack>
                  <ContentBox p="md" w="100%" gap="lg">
                    <Text size="lg">What to have an automated position?</Text>
                    <Text size="sm" opacity={0.7}>
                      We recommend using the {symbol} Vault to maximize returns.
                    </Text>
                    <Button onClick={() => set_active_tab('deposit')}>
                      Get started by switching to Deposit
                    </Button>
                  </ContentBox>

                  <Alert
                    variant="light"
                    color="blue"
                    title="Outside the vault range"
                    icon={<IconInfoCircle />}
                  >
                    <Text size="sm">
                      Manual positions have to be outside the vault range.
                    </Text>
                  </Alert>
                  <ManualPosition
                    fund_symbol={fund_symbol}
                    onCompleted={onCompleted}
                  />
                </Stack>
              </Tabs.Panel>
            </Stack>
            <Stack flex="1 1 100vw" maw="44.4rem">
              <Stack gap="xl">
                <ContentBox
                  w="100%"
                  h="450px"
                  pl="sm"
                  pt="lg"
                  pr="lg"
                  pb="xxs"
                  pos="relative"
                >
                  <VaultPerShareChart symbol={symbol} />
                </ContentBox>
                <ContentWithTitle title="Return Metrics">
                  <VaultReturnMetrics symbol={symbol} />
                </ContentWithTitle>
                <ContentWithTitle title="About">
                  <Text size="sm" opacity={0.6}>
                    The vault automates liquidity provisioning to our exchange.
                    Provide Liquidity using {fund_symbol} and USDC in our vault,
                    earn trading fees and redeem at any time. Holding assets in
                    our vault earns users governance token rewards.
                  </Text>
                </ContentWithTitle>
                <ContentWithTitle title={`${vault.symbol} Holdings`}>
                  <Holdings symbol={symbol} />
                </ContentWithTitle>
              </Stack>
            </Stack>
          </Group>
        </Tabs>
      </Stack>
    </>
  )
}
